'use client'
import { useState, useEffect, useCallback } from 'react';

/**
 * Hook to wrap Supabase data fetchers with loading/error state.
 * The fetchFn should be an existing cached function like () => getLeaderboard(tournamentId)
 * Caching is handled inside supabaseData.js — this hook just manages React state.
 */
export function useSupabaseData(fetchFn, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchFn();
      setData(result);
    } catch (err) {
      console.error('useSupabaseData error:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => fetch();
    window.addEventListener('cacheCleared', handler);
    return () => window.removeEventListener('cacheCleared', handler);
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
}
